export interface HighlightWordWindow {
  startFrame: number;
  endFrame: number;
}

export interface HighlightWordMotion {
  transform: string;
  transformOrigin: string;
  zIndex: number;
  opacity?: number;
  fontSize?: string;
  filter?: string;
}

/** Painted on the word's text only, so leading/trailing spaces stay bare. */
export interface WordTextDecoration {
  backgroundImage: string;
  backgroundSize: string;
  backgroundPosition: string;
  backgroundRepeat: string;
}

export interface LineEntranceMotion {
  filter: string;
  opacity: number;
}

const SCALE_UP_PEAK = 1.1;
const SCALE_UP_ATTACK_FRAMES = 12;
const PUNCH_PEAK = 1.1;
const PUNCH_SETTLE = 1.06;
const PUNCH_UP_FRAMES = 8;
const PUNCH_DOWN_FRAMES = 12;
const JUMP_HEIGHT_PX = 18;
const JUMP_PEAK = 1.1;
const JUMP_FRAMES = 18;
const PULSE_PEAK = 1.1;
const PULSE_PERIOD_FRAMES = 12;
const RISE_PX = 16;
const RISE_PEAK = 1.06;
const DROP_PX = 24;
const DROP_SQUASH_Y = 0.92;
const DROP_FRAMES = 10;
const SLIDE_FRAMES = 14;
const SWING_DEG = 12;
const WIGGLE_DEG = 7;
const WIGGLE_PERIOD_FRAMES = 15;
const SQUASH_X = 1.1;
const SQUASH_Y = 0.88;
const SQUASH_ATTACK_FRAMES = 8;
const SKEW_DEG = 10;
const SKEW_FRAMES = 16;
const DRIFT_PX = 8;
const DRIFT_PERIOD_FRAMES = 28;
const WAVE_DEG = 8;
const WAVE_PX = 8;
const WAVE_PERIOD_FRAMES = 32;
const FLICKER_DIM = 0.25;
const FLICKER_PERIOD_FRAMES = 12;
const SCALE_CAP = 1.1;
// Offsets below are in em so they follow the subtitle font size — the same
// look at 1080p, at 720p and in the small animation-carousel previews.
const GLOW_ATTACK_FRAMES = 6;
const GLOW_RADIUS_EM = 0.3;
const GLOW_PULSE_PERIOD_FRAMES = 20;
const GLOW_PULSE_DEPTH = 0.3;
const SHAKE_EM = 0.035;
const SHAKE_DEG = 2;
const SHAKE_ATTACK_FRAMES = 3;
const SHAKE_STEP_FRAMES = 2;
const GLITCH_FRAMES = 8;
const GLITCH_SPLIT_EM = 0.08;
const GLITCH_JITTER_EM = 0.04;
const GLITCH_RED = '#ff0050';
const GLITCH_CYAN = '#00e5ff';
const UNDERLINE_FRAMES = 8;
const UNDERLINE_THICKNESS_EM = 0.1;
const REVEAL_FRAMES = 8;
const BLUR_IN_EM = 0.25;
const SLIDE_UP_EM = 0.5;
const SLAM_FROM = 1.5;
const SLAM_UNDERSHOOT = 0.94;
const SLAM_FRAMES = 6;
const SLAM_SETTLE_FRAMES = 4;
const FLIP_FRAMES = 10;
const FLIP_PERSPECTIVE_EM = 4;
const LINE_BLUR_IN_EM = 0.3;
const LINE_BLUR_IN_FRAMES = 10;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function attackProgress(
  frame: number,
  word: HighlightWordWindow,
  attackFrames: number,
): number {
  const wordSpan = Math.max(1, word.endFrame - word.startFrame);
  const span = Math.max(1, Math.min(attackFrames, wordSpan));
  return clamp01((frame - word.startFrame) / span);
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function easeInOutCubic(t: number): number {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - ((-2 * x + 2) ** 3) / 2;
}

function smoothHop(t: number): number {
  return 0.5 - 0.5 * Math.cos(Math.PI * 2 * clamp01(t));
}

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * clamp01(t);
}

/** Rounds for CSS so tiny values never serialize as `1e-7em`. */
function css(value: number): number {
  return Number(value.toFixed(4));
}

/** Deterministic noise in [-1, 1]: renders must match frame for frame. */
function noise(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}

function scaleUpAmount(frame: number, word: HighlightWordWindow): number {
  return lerp(1, SCALE_UP_PEAK, easeOutCubic(attackProgress(frame, word, SCALE_UP_ATTACK_FRAMES)));
}

export function scaleUpMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  return {
    transform: `scale(${scaleUpAmount(frame, word)})`,
    transformOrigin: 'center center',
    zIndex: 2,
  };
}

export function scaleUpPushMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  return {
    transform: 'none',
    fontSize: `${scaleUpAmount(frame, word)}em`,
    transformOrigin: 'center center',
    zIndex: 2,
  };
}

export function punchMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  let scale = PUNCH_SETTLE;
  if (elapsed <= PUNCH_UP_FRAMES) {
    scale = lerp(1, PUNCH_PEAK, easeInOutCubic(elapsed / PUNCH_UP_FRAMES));
  } else if (elapsed <= PUNCH_UP_FRAMES + PUNCH_DOWN_FRAMES) {
    scale = lerp(
      PUNCH_PEAK,
      PUNCH_SETTLE,
      easeInOutCubic((elapsed - PUNCH_UP_FRAMES) / PUNCH_DOWN_FRAMES),
    );
  }
  return {
    transform: `scale(${scale})`,
    transformOrigin: 'center center',
    zIndex: 2,
  };
}

export function jumpMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const hop = smoothHop(attackProgress(frame, word, JUMP_FRAMES));
  return {
    transform: `translateY(${-JUMP_HEIGHT_PX * hop}px) scale(${lerp(1, JUMP_PEAK, hop)})`,
    transformOrigin: 'center bottom',
    zIndex: 2,
  };
}

export function pulseMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  const hop = 0.5 - 0.5 * Math.cos((elapsed / PULSE_PERIOD_FRAMES) * Math.PI * 2);
  return {
    transform: `scale(${lerp(1, PULSE_PEAK, hop)})`,
    transformOrigin: 'center center',
    zIndex: 2,
  };
}

function remainingSlide(frame: number, word: HighlightWordWindow): number {
  return 1 - easeOutCubic(attackProgress(frame, word, SLIDE_FRAMES));
}

export function riseMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const p = easeOutCubic(attackProgress(frame, word, SLIDE_FRAMES));
  return {
    transform: `translateY(${-RISE_PX * p}px) scale(${lerp(1, RISE_PEAK, p)})`,
    transformOrigin: 'center bottom',
    zIndex: 2,
  };
}

export function dropMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const p = easeOutCubic(attackProgress(frame, word, DROP_FRAMES));
  return {
    transform: `translateY(${DROP_PX * p}px) scale(1, ${lerp(1, DROP_SQUASH_Y, p)})`,
    transformOrigin: 'center top',
    zIndex: 2,
  };
}

export function swingMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  return {
    transform: `rotate(${-SWING_DEG * remainingSlide(frame, word)}deg)`,
    transformOrigin: 'center top',
    zIndex: 2,
  };
}

export function wiggleMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  const angle = WIGGLE_DEG * Math.sin((elapsed / WIGGLE_PERIOD_FRAMES) * Math.PI * 2);
  return {
    transform: `rotate(${angle}deg)`,
    transformOrigin: 'center bottom',
    zIndex: 2,
  };
}

export function squashMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const p = easeOutCubic(attackProgress(frame, word, SQUASH_ATTACK_FRAMES));
  const x = lerp(1, SQUASH_X, p);
  const y = lerp(1, SQUASH_Y, p);
  return {
    transform: `scale(${x}, ${y})`,
    transformOrigin: 'center bottom',
    zIndex: 2,
  };
}

export function skewMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const hop = smoothHop(attackProgress(frame, word, SKEW_FRAMES));
  return {
    transform: `skewX(${SKEW_DEG * hop}deg)`,
    transformOrigin: 'center center',
    zIndex: 2,
  };
}

export function driftMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  const x = DRIFT_PX * Math.sin((elapsed / DRIFT_PERIOD_FRAMES) * Math.PI * 2);
  return {
    transform: `translateX(${x}px)`,
    transformOrigin: 'center center',
    zIndex: 2,
  };
}

export function elasticMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  const bounce = Math.exp(-elapsed / 8) * Math.cos((elapsed / 5) * Math.PI);
  const scale = Math.min(SCALE_CAP, 1 + 0.1 * (1 - bounce));
  return {
    transform: `scale(${scale})`,
    transformOrigin: 'center center',
    zIndex: 2,
  };
}

export function waveMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  const wave = Math.sin((elapsed / WAVE_PERIOD_FRAMES) * Math.PI * 2);
  return {
    transform: `translateY(${-WAVE_PX * wave}px) rotate(${WAVE_DEG * wave}deg)`,
    transformOrigin: 'center bottom',
    zIndex: 2,
  };
}

export function flickerMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  const hop = 0.5 - 0.5 * Math.cos((elapsed / FLICKER_PERIOD_FRAMES) * Math.PI * 2);
  return {
    transform: 'none',
    transformOrigin: 'center center',
    zIndex: 2,
    opacity: 1 - FLICKER_DIM * hop,
  };
}

export function glowMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  const attack = easeOutCubic(attackProgress(frame, word, GLOW_ATTACK_FRAMES));
  const dip = 0.5 - 0.5 * Math.cos((elapsed / GLOW_PULSE_PERIOD_FRAMES) * Math.PI * 2);
  const radius = GLOW_RADIUS_EM * attack * (1 - GLOW_PULSE_DEPTH * dip);
  // drop-shadow (not text-shadow) so the glow wraps the inherited outline
  // instead of replacing it; currentColor is the word's highlight color.
  return {
    transform: 'none',
    transformOrigin: 'center center',
    zIndex: 2,
    filter: `drop-shadow(0em 0em ${css(radius * 0.4)}em currentColor) drop-shadow(0em 0em ${css(radius)}em currentColor)`,
  };
}

export function shakeMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  const amount = easeOutCubic(attackProgress(frame, word, SHAKE_ATTACK_FRAMES));
  const seed = (word.startFrame * 31 + Math.floor(elapsed / SHAKE_STEP_FRAMES)) * 3;
  const x = SHAKE_EM * amount * noise(seed + 1);
  const y = SHAKE_EM * amount * noise(seed + 2);
  const angle = SHAKE_DEG * amount * noise(seed + 3);
  return {
    transform: `translate(${css(x)}em, ${css(y)}em) rotate(${css(angle)}deg)`,
    transformOrigin: 'center center',
    zIndex: 2,
  };
}

export function glitchMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  if (elapsed >= GLITCH_FRAMES) {
    return {
      transform: 'none',
      transformOrigin: 'center center',
      zIndex: 2,
      filter: 'none',
    };
  }
  const decay = 1 - elapsed / GLITCH_FRAMES;
  const seed = (word.startFrame * 17 + elapsed) * 2;
  const direction = elapsed % 2 === 0 ? 1 : -1;
  const split = direction * GLITCH_SPLIT_EM * decay * (0.6 + 0.4 * Math.abs(noise(seed + 1)));
  const jitter = GLITCH_JITTER_EM * decay * noise(seed + 2);
  return {
    transform: `translate(${css(jitter)}em, 0em)`,
    transformOrigin: 'center center',
    zIndex: 2,
    filter: `drop-shadow(${css(split)}em 0em 0em ${GLITCH_RED}) drop-shadow(${css(-split)}em 0em 0em ${GLITCH_CYAN})`,
  };
}

export function underlineMotion(
  frame: number,
  word: HighlightWordWindow,
): WordTextDecoration {
  const p = easeOutCubic(attackProgress(frame, word, UNDERLINE_FRAMES));
  return {
    backgroundImage: 'linear-gradient(currentColor, currentColor)',
    backgroundSize: `${css(p * 100)}% ${UNDERLINE_THICKNESS_EM}em`,
    backgroundPosition: '0 100%',
    backgroundRepeat: 'no-repeat',
  };
}

/** How much of the word is painted in the highlight color (0..1), linear in time. */
export function karaokeFillProgress(
  frame: number,
  word: HighlightWordWindow,
): number {
  if (word.endFrame <= word.startFrame) {
    return frame >= word.startFrame ? 1 : 0;
  }
  return clamp01((frame - word.startFrame) / (word.endFrame - word.startFrame));
}

export function blurInMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const p = easeOutCubic(attackProgress(frame, word, REVEAL_FRAMES));
  const blur = BLUR_IN_EM * (1 - p);
  return {
    transform: 'none',
    transformOrigin: 'center center',
    zIndex: 2,
    filter: blur > 0 ? `blur(${css(blur)}em)` : 'none',
    opacity: css(p),
  };
}

export function slideUpMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const p = easeOutCubic(attackProgress(frame, word, REVEAL_FRAMES));
  return {
    transform: `translateY(${css(SLIDE_UP_EM * (1 - p))}em)`,
    transformOrigin: 'center center',
    zIndex: 2,
    opacity: css(p),
  };
}

export function slamMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const elapsed = Math.max(0, frame - word.startFrame);
  const slamFrames = Math.max(1, Math.min(SLAM_FRAMES, word.endFrame - word.startFrame));
  let scale: number;
  if (elapsed < slamFrames) {
    const t = elapsed / slamFrames;
    scale = lerp(SLAM_FROM, SLAM_UNDERSHOOT, t * t);
  } else {
    scale = lerp(
      SLAM_UNDERSHOOT,
      1,
      easeOutCubic((elapsed - slamFrames) / SLAM_SETTLE_FRAMES),
    );
  }
  return {
    transform: `scale(${css(scale)})`,
    transformOrigin: 'center center',
    zIndex: 2,
    opacity: css(clamp01(elapsed / 2)),
  };
}

export function flipInMotion(
  frame: number,
  word: HighlightWordWindow,
): HighlightWordMotion {
  const p = easeOutCubic(attackProgress(frame, word, FLIP_FRAMES));
  return {
    transform: `perspective(${FLIP_PERSPECTIVE_EM}em) rotateX(${css(90 * (1 - p))}deg)`,
    transformOrigin: 'center center',
    zIndex: 2,
    opacity: css(clamp01(p * 2)),
  };
}

export function blurInLineMotion(
  frame: number,
  line: HighlightWordWindow,
): LineEntranceMotion {
  const p = easeOutCubic(attackProgress(frame, line, LINE_BLUR_IN_FRAMES));
  const blur = LINE_BLUR_IN_EM * (1 - p);
  return {
    filter: blur > 0 ? `blur(${css(blur)}em)` : 'none',
    opacity: css(p),
  };
}
